const sandlotFitSize = value => {
  const text = String(value || '').toUpperCase().replace(/[–—]/g, '-');
  if (/\bL\s*[-/]\s*XL\b|LARGE\s*[-/]\s*(?:EXTRA LARGE|XL)/.test(text)) return 'L-XL';
  if (/\bM\s*[-/]\s*L\b|MEDIUM\s*[-/]\s*LARGE/.test(text)) return 'M-L';
  return '';
};
const sandlotFitClosure = value => {
  const text = String(value || '').toLowerCase();
  const leather = /leather/.test(text);
  const snap = /snap[ -]?back/.test(text);
  return leather === snap ? '' : leather ? 'leather' : 'snap';
};
const sandlotFitKey = spec => `${spec.profile}:${spec.size}:${spec.closure === 'strap' ? 'leather' : spec.closure}`;
function sandlotVintageConfiguration(product, variant) {
  if (!variant) return '';
  const values = variant.options || [];
  const fitValues = values.filter((value, index) => !/colou?r/i.test(product.options[index] || ''));
  const sizeValue = values.find((value, index) => /size/i.test(product.options[index] || ''));
  const size = sandlotFitSize(fitValues.join(' '));
  if (sizeValue && !size && !/^(one size|os|osfa|default title)$/i.test(sizeValue)) return '';
  const explicitClosure = sandlotFitClosure(fitValues.join(' '));
  const hasClosureOptions = product.variants.some(item => item.options.some((value, index) => !/colou?r/i.test(product.options[index] || '') && sandlotFitClosure(value)));
  const fallback = hasClosureOptions ? '' : sandlotFitClosure((product.tags || []).join(' ')) || sandlotFitClosure(product.description);
  const closure = explicitClosure || (size === 'M-L' ? 'snap' : fallback);
  const resolvedSize = size || (closure === 'leather' ? 'L-XL' : '');
  if (resolvedSize === 'M-L' && closure === 'snap') return '1735:M-L:snap';
  if (resolvedSize === 'L-XL' && ['leather','snap'].includes(closure)) return `1735:L-XL:${closure}`;
  return '';
}
function sandlotVintageOptions(product, choices = []) {
  return new Set(product.variants.filter(variant => choices.every((choice, index) => {
    const name = product.options[index] || '';
    const isFit = /size|closure|fit/i.test(name) || product.variants.some(item => sandlotFitSize(item.options[index]) || sandlotFitClosure(item.options[index]));
    return !choice || isFit || variant.options[index] === choice;
  })).map(variant => sandlotVintageConfiguration(product, variant)).filter(Boolean));
}
class SandlotFitGuide extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.images = JSON.parse(this.querySelector('[data-fit-images]').textContent);
    this.specs = JSON.parse(this.querySelector('[data-fit-data]').textContent);
    if (!this.specs.length) return;
    this.product = JSON.parse(this.querySelector('[data-fit-product]').textContent);
    const purchase = document.querySelector(`sandlot-product[data-product-id="${this.dataset.productId}"]`);
    this.variantId = purchase?.current?.id || this.product.selected;
    this.choices = purchase?.fields?.map(field => field.querySelector('input:checked')?.value) || this.product.variants.find(variant => String(variant.id) === String(this.variantId))?.options || [];
    this.intro = this.querySelector('[data-fit-choose]').textContent;
    this.profile = this.querySelector('[data-fit-profile]');
    this.size = this.querySelector('[data-fit-size]');
    this.units = this.querySelector('[data-fit-units]');
    const profiles = [...new Map(this.specs.map(spec => [spec.profile,spec.name])).entries()];
    this.profile.replaceChildren();
    profiles.forEach(([value,name]) => this.profile.add(new Option(name,value)));
    if (!profiles.some(([value]) => value === this.dataset.initialProfile)) {
      const choose = new Option(this.dataset.chooseLabel,'');
      choose.disabled = true;
      this.profile.prepend(choose);
    }
    const profileButtons = profiles.map(([value,name]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = name.replace(/^\S+\s+/, '');
      button.dataset.fitProfileButton = value;
      button.addEventListener('click', () => { this.profile.value = value; this.selectProfile(); });
      return button;
    });
    this.querySelector('[data-fit-profiles]').replaceChildren(...profileButtons);
    this.profile.value = this.dataset.initialProfile;
    this.units.value = 'in';
    this.profile.disabled = false;
    this.profile.addEventListener('change', () => this.selectProfile());
    this.size.addEventListener('change', () => this.selectSpec());
    this.units.addEventListener('change', () => { if (this.spec) this.render(); });
    this.querySelectorAll('[data-fit-step]').forEach(button => button.addEventListener('click', () => {
      this.position = Math.max(0,Math.min(this.spec.positions - 1,this.position + Number(button.dataset.fitStep)));
      this.render();
    }));
    this.onVariant = event => {
      if (event.target.dataset.productId !== this.dataset.productId) return;
      this.variantId = event.detail.variantId;
      this.choices = event.detail.options || [];
      if (this.profile.value === this.dataset.initialProfile) this.selectProfile(event.detail.size);
    };
    document.addEventListener('sandlot:fit-size-change', this.onVariant);
    this.selectProfile(this.dataset.initialSize);
  }
  disconnectedCallback() { document.removeEventListener('sandlot:fit-size-change',this.onVariant); this.ready = false; }
  selectProfile(preferredSize) {
    this.querySelectorAll('[data-fit-profile-button]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fitProfileButton === this.profile.value)));
    let specs = this.specs.filter(spec => spec.profile === this.profile.value);
    const vintageProduct = this.profile.value === '1735' && this.dataset.initialProfile === '1735';
    if (vintageProduct) {
      const offered = sandlotVintageOptions(this.product, this.choices);
      specs = specs.filter(spec => offered.has(sandlotFitKey(spec)));
      const selected = this.product.variants.find(variant => String(variant.id) === String(this.variantId));
      const key = sandlotVintageConfiguration(this.product, selected);
      if (selected && !key) specs = [];
      preferredSize = key || this.size.value;
    }
    this.querySelector('[data-fit-choose]').textContent = vintageProduct ? this.dataset.unmatchedLabel : this.intro;
    this.querySelector('[data-fit-specs]').hidden = !specs.length;
    this.querySelector('.sg-fit__range').hidden = !specs.length;
    this.querySelector('.sg-fit__stepper').hidden = !specs.length;
    this.querySelector('.sg-fit__footer').hidden = !specs.length;
    this.querySelector('[data-fit-choose]').hidden = !!specs.length;
    this.querySelector('[data-fit-size-wrap]').hidden = !specs.length || (this.profile.value !== '1735' && specs.length < 2);
    if (!specs.length) { this.spec = null; this.size.replaceChildren(); this.size.disabled = true; return; }
    this.size.replaceChildren();
    specs.forEach(spec => {
      const label = spec.profile === '1735' ? `${spec.size} · ${this.dataset[`${spec.closure === 'strap' ? 'leather' : spec.closure}Closure`]}` : spec.size;
      this.size.add(new Option(label, sandlotFitKey(spec)));
    });
    const preferred = specs.find(spec => sandlotFitKey(spec) === preferredSize || spec.size === preferredSize);
    if (preferred) this.size.value = sandlotFitKey(preferred);
    this.size.disabled = false;
    this.selectSpec();
  }
  selectSpec() {
    this.spec = this.specs.find(spec => spec.profile === this.profile.value && sandlotFitKey(spec) === this.size.value);
    this.spec.positions = Math.max(1,Math.min(12,Number(this.spec.positions)||7));
    this.position = Math.floor((this.spec.positions - 1) / 2);
    const marks = this.querySelector('[data-fit-marks]');
    marks.replaceChildren(...Array.from({length:this.spec.positions},()=>document.createElement('i')));
    this.render();
  }
  render() {
    const spec = this.spec;
    const labels = (spec.labels || '').split(',');
    const snapCount = spec.closure === 'snap' ? Number((spec.snapCounts || '').split(',')[this.position]) : 0;
    const measuredSnap = Number.isInteger(snapCount) && snapCount >= 2 && snapCount <= 7;
    this.querySelector('.sg-fit__closure-note').hidden = !!spec.verified && measuredSnap;
    const positionName = spec.closure === 'snap' ? this.dataset.snapLabel : this.dataset.strapLabel;
    this.querySelector('[data-fit-position]').textContent = labels[this.position]?.trim() || '—';
    this.querySelector('[data-fit-count]').textContent = measuredSnap ? `${snapCount} ${this.dataset.snapsEngagedLabel}` : `${positionName} ${this.position + 1} ${this.dataset.ofLabel} ${spec.positions}`;
    const positionNote = (spec.positionNotes || '').split('|')[this.position]?.trim();
    if (positionNote) this.querySelector('[data-fit-count]').textContent += ` — ${positionNote}`;
    this.querySelector('[data-fit-step="-1"]').disabled = this.position === 0;
    this.querySelector('[data-fit-step="1"]').disabled = this.position === spec.positions - 1;
    [...this.querySelector('[data-fit-marks]').children].forEach((mark,index)=>mark.classList.toggle('is-active',index === this.position));
    const closure = spec.closure === 'strap' ? (spec.profile === '1810' ? 'self' : 'leather') : spec.closure;
    const type = this.images[closure] ? closure : 'leather';
    const lastFrame = this.images[type].length - 1;
    const frame = spec.positions === 1 ? Math.round(lastFrame / 2) : lastFrame - Math.round(this.position * lastFrame / (spec.positions - 1));
    const caption = this.dataset[`${type}Closure`];
    this.querySelector('[data-fit-closure]').textContent = caption;
    const image = this.querySelector('[data-fit-closure-image]');
    image.src = measuredSnap ? this.images.snapMeasured[snapCount - 2] : this.images[type][frame];
    const snapOffsets = [5,3,2,1,0,-1,-2];
    const centerShift = type === 'self' ? (-20 - 12 * frame * 6 / lastFrame) / 640 : type === 'leather' ? 0 : (56 - 22 * (measuredSnap ? 7 - snapCount : snapOffsets[frame])) / 720;
    image.style.setProperty('--fit-center-shift', `${centerShift * 100}%`);
    image.alt = `${caption} — ${this.querySelector('[data-fit-count]').textContent}`;
    const values = {circumference:(spec.circumferences || '').split(',')[this.position],crown:spec.crown,length:spec.length,width:spec.width,vent_width:(spec.ventWidths || '').split(',')[this.position],vent_height:spec.ventHeight};
    this.querySelectorAll('[data-fit-measurement]').forEach(element => {
      const number = Number(values[element.dataset.fitMeasurement]);
      const known = (spec.verified || this.dataset.showSamples === 'true') && Number.isFinite(number) && number > 0;
      const units = this.units.value;
      const converted = spec.units === units ? number : units === 'in' ? number / 2.54 : number * 2.54;
      element.textContent = known ? `${new Intl.NumberFormat(document.documentElement.lang || 'en',{maximumFractionDigits:units === 'in' ? 3 : 1}).format(converted)}${element.tagName.toLowerCase() === 'text' && units === 'in' ? '"' : ' '+units}` : this.dataset.pendingLabel;
      element.classList.toggle('is-pending',!known);
    });
    this.querySelector('[data-fit-verification]').textContent = spec.verified ? this.dataset.verifiedLabel : this.dataset.showSamples === 'true' ? this.dataset.sampleLabel : this.dataset.previewLabel;
  }
}
if (!customElements.get('sandlot-fit-guide')) customElements.define('sandlot-fit-guide',SandlotFitGuide);
